import { useEffect, useState, useMemo, useRef } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  fetchParkingSpaces,
  fetchContacts,
  createParkingSpace,
  updateParkingSpace,
  deleteParkingSpace,
} from '../../store/slices/financeSlice'
import type { ParkingSpace, ParkingSpaceCreateInput, ParkingSpaceUpdateInput, ParkingType, Contact } from '@/types/finance'
import { useToast } from '../../components/ToastContainer'
import {
  TruckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ArrowLeftIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  ArrowUpTrayIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import {
  importParkingFromExcel,
  ParkingExcelImportResult,
  getParkingImportLogs,
  getParkingImportLogDetail,
  deleteParkingImportLog as apiDeleteImportLog,
  ParkingImportLog,
  ParkingImportLogDetail,
  getUnits,
  activateBikePair,
  deactivateBikePair,
  getSpaceLeases,
  createSpaceLease,
} from '../../services/api/finance'
import type { ParkingLease } from '@/types/finance'
import type { Unit } from '@/types/finance'
import { useFinancePageSize, FINANCE_PAGE_SIZE_OPTIONS } from '../../hooks/useFinancePageSize'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import { FinanceAuditLog } from '../../components/finance'
import { ClockIcon, EyeIcon, DocumentTextIcon } from '@heroicons/react/24/outline'

// Helper function to get unit codes from contact
const getUnitCodes = (contact: Contact): string => {
  const unitContacts = contact.unitContacts || []
  if (unitContacts.length === 0) return ''
  return unitContacts.map(uc => uc.unit?.unitCode).filter(Boolean).join(', ')
}

// Searchable Contact Select Component
interface SearchableContactSelectProps {
  value: string
  onChange: (value: string) => void
  contacts: Contact[]
  placeholder?: string
  required?: boolean
  allowEmpty?: boolean
  emptyLabel?: string
}

const SearchableContactSelect = ({
  value,
  onChange,
  contacts,
  placeholder = '請選擇',
  required = false,
  allowEmpty = false,
  emptyLabel = '請選擇',
}: SearchableContactSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!search) return contacts
    const searchLower = search.toLowerCase()
    return contacts.filter(contact => {
      const unitCodes = getUnitCodes(contact).toLowerCase()
      const name = contact.name.toLowerCase()
      return name.includes(searchLower) || unitCodes.includes(searchLower)
    })
  }, [contacts, search])

  // Get display text for selected value
  const selectedContact = contacts.find(c => c.id === value)
  const displayText = selectedContact
    ? `${getUnitCodes(selectedContact) ? getUnitCodes(selectedContact) + ' - ' : ''}${selectedContact.name}`
    : ''

  const handleSelect = (contactId: string) => {
    onChange(contactId)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen)
          if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100)
          }
        }}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between ${
          !value && required ? 'text-gray-400' : 'text-gray-900'
        }`}
      >
        <span className="truncate">{displayText || placeholder}</span>
        <ChevronUpDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜尋戶號或姓名..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-60">
            {allowEmpty && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full px-3 py-2 text-left hover:bg-gray-100 text-sm ${
                  value === '' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {emptyLabel}
              </button>
            )}
            {filteredContacts.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                找不到符合的聯絡人
              </div>
            ) : (
              filteredContacts.map((contact) => {
                const unitCodes = getUnitCodes(contact)
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleSelect(contact.id)}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 text-sm ${
                      value === contact.id ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      {unitCodes && (
                        <span className="text-blue-600 font-medium mr-2">{unitCodes}</span>
                      )}
                      <span>{contact.name}</span>
                      {contact.phone && (
                        <span className="text-gray-400 ml-2">({contact.phone})</span>
                      )}
                    </div>
                  </button>
                )
              })
            )}
          </div>

          {/* Result count */}
          <div className="px-3 py-2 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
            共 {filteredContacts.length} 筆結果
          </div>
        </div>
      )}
    </div>
  )
}

interface ParkingFormData {
  spaceCode: string
  type: ParkingType
  ownerId: string
  currentUserId: string
  currentUserLeaseStart: string
  currentUserLeaseEnd: string
  unitId: string
  licensePlates: string
  note: string
}

const initialFormData: ParkingFormData = {
  spaceCode: '',
  type: 'CAR',
  ownerId: '',
  currentUserId: '',
  currentUserLeaseStart: '',
  currentUserLeaseEnd: '',
  unitId: '',
  licensePlates: '',
  note: '',
}

const parkingTypeLabels: Record<ParkingType, string> = {
  CAR: '汽車位',
  MOTOR: '機車位',
  BIKE: '腳踏車位',
  DISABLED: '無障礙車位',
}

const parkingTypeColors: Record<ParkingType, string> = {
  CAR: 'bg-blue-100 text-blue-800',
  MOTOR: 'bg-green-100 text-green-800',
  BIKE: 'bg-yellow-100 text-yellow-800',
  DISABLED: 'bg-purple-100 text-purple-800',
}

// 每頁筆數選項（共用於所有財務模組頁面）
const PAGE_SIZE_OPTIONS = FINANCE_PAGE_SIZE_OPTIONS

const ParkingSpaceList = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    parkingSpaces,
    parkingSpacesPagination,
    parkingSpacesTypeCounts,
    parkingSpacesLoading,
    parkingSpacesError,
    contacts,
  } = useAppSelector(state => state.finance)

  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useFinancePageSize()

  // 篩選狀態
  const { hasPermission } = usePermissions(currentProject?.id)
  const canView = hasPermission(Permission.VIEW_FINANCE_PARKING)
  const canManage = hasPermission(Permission.MANAGE_FINANCE_PARKING)
  const canDelete = hasPermission(Permission.DELETE_FINANCE_PARKING)

  const [typeFilter, setTypeFilter] = useState<ParkingType | ''>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingSpace, setEditingSpace] = useState<ParkingSpace | null>(null)
  const [formData, setFormData] = useState<ParkingFormData>(initialFormData)
  const [submitting, setSubmitting] = useState(false)
  // 所有戶別（供編輯 modal 下拉）
  const [allUnits, setAllUnits] = useState<Unit[]>([])
  const [unitSearch, setUnitSearch] = useState('')
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false)

  // 活化配對選擇模式（勾選 2 個 BIKE 配對）
  const [activationMode, setActivationMode] = useState(false)
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>([])
  // 活化配對展開狀態（key = 配對中較小編號的 spaceId）
  const [expandedPairKeys, setExpandedPairKeys] = useState<Set<string>>(new Set())
  // 租約歷史 modal
  const [leaseHistorySpace, setLeaseHistorySpace] = useState<ParkingSpace | null>(null)

  // 新增排程租約表單狀態
  const [showAddLeaseForm, setShowAddLeaseForm] = useState(false)
  const [newLeaseData, setNewLeaseData] = useState({
    leaseStartDate: '',
    leaseEndDate: '',
    unitId: '',
    contactId: '',
    note: '',
    applyToPair: false,
  })
  const [newLeaseUnitSearch, setNewLeaseUnitSearch] = useState('')
  const [newLeaseUnitDropdownOpen, setNewLeaseUnitDropdownOpen] = useState(false)
  const [savingLease, setSavingLease] = useState(false)

  // Import Excel states
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [updateExisting, setUpdateExisting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<ParkingExcelImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Import logs states
  const [showImportLogsModal, setShowImportLogsModal] = useState(false)
  const [importLogs, setImportLogs] = useState<ParkingImportLog[]>([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [selectedLogDetail, setSelectedLogDetail] = useState<ParkingImportLogDetail | null>(null)
  const [showLogDetailModal, setShowLogDetailModal] = useState(false)
  const [loadingLogDetail, setLoadingLogDetail] = useState(false)

  // 搜尋防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1) // 搜尋時重置到第一頁
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // 載入車位資料
  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchParkingSpaces({
        projectId: currentProject.id,
        page: currentPage,
        limit: pageSize,
        type: typeFilter || undefined,
        search: debouncedSearch || undefined,
      }))
    }
  }, [dispatch, currentProject?.id, currentPage, pageSize, typeFilter, debouncedSearch])

  // 載入聯絡人資料（用於新增/編輯表單）
  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchContacts({ projectId: currentProject.id, params: { limit: 2000 } }))
    }
  }, [dispatch, currentProject?.id])

  // 載入全部戶別（編輯 modal 用）
  useEffect(() => {
    if (!currentProject?.id) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await getUnits(currentProject.id, { page: 1, limit: 2000 })
        if (!cancelled && res.success && res.data) {
          setAllUnits(res.data.units)
        }
      } catch (err: any) {
        console.error('Failed to load units for parking form:', err)
      }
    })()
    return () => { cancelled = true }
  }, [currentProject?.id])

  // 處理 ?edit=<id> 參數（從首頁警示點擊過來）
  useEffect(() => {
    const editId = searchParams.get('edit')
    if (editId && parkingSpaces.length > 0 && !showModal) {
      const space = parkingSpaces.find(s => s.id === editId)
      if (space) {
        openEditModal(space)
        setSearchParams({}, { replace: true })
      }
    }
  }, [searchParams, parkingSpaces, showModal])

  // 當篩選條件改變時重置到第一頁
  const handleTypeFilterChange = (type: ParkingType | '') => {
    setTypeFilter(type)
    setCurrentPage(1)
  }

  // 頁碼改變
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // 每頁筆數改變
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1) // 重置到第一頁
  }

  // 清除搜尋
  const clearSearch = () => {
    setSearchQuery('')
    setDebouncedSearch('')
    setTypeFilter('')
    setCurrentPage(1)
  }

  const openCreateModal = () => {
    setFormData(initialFormData)
    setIsEditing(false)
    setEditingSpace(null)
    setShowModal(true)
  }

  const openEditModal = (space: ParkingSpace) => {
    setFormData({
      spaceCode: space.spaceCode,
      type: space.type,
      ownerId: space.ownerId || '',
      currentUserId: space.currentUserId || '',
      currentUserLeaseStart: space.currentUserLeaseStart ? String(space.currentUserLeaseStart).slice(0, 10) : '',
      currentUserLeaseEnd: space.currentUserLeaseEnd ? String(space.currentUserLeaseEnd).slice(0, 10) : '',
      unitId: space.unitId || '',
      licensePlates: space.licensePlates.join(', '),
      note: space.note || '',
    })
    setEditingSpace(space)
    setIsEditing(true)
    setShowModal(true)
    // 載入租約歷史（BIKE / DISABLED 才有意義）
    if ((space.type === 'BIKE' || space.type === 'DISABLED') && currentProject?.id) {
      setLeaseHistoryItems([])
      setLeaseHistoryLoading(true)
      getSpaceLeases(currentProject.id, space.id)
        .then(res => {
          if (res.success && res.data) setLeaseHistoryItems(res.data.leases)
        })
        .catch(err => showError('載入租約歷史失敗', err.message || '請稍後再試'))
        .finally(() => setLeaseHistoryLoading(false))
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingSpace(null)
    setFormData(initialFormData)
    setUnitSearch('')
    setUnitDropdownOpen(false)
    setShowAddLeaseForm(false)
    setNewLeaseData({ leaseStartDate: '', leaseEndDate: '', unitId: '', contactId: '', note: '', applyToPair: false })
    setNewLeaseUnitSearch('')
    setNewLeaseUnitDropdownOpen(false)
    setLeaseHistoryItems([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentProject?.id) return

    if (!formData.spaceCode.trim() || !formData.unitId) {
      showError('請填寫必填欄位（車位編號與關聯戶別）')
      return
    }
    if (formData.currentUserLeaseStart && formData.currentUserLeaseEnd && formData.currentUserLeaseStart > formData.currentUserLeaseEnd) {
      showError('租約起始日不可晚於結束日')
      return
    }

    setSubmitting(true)
    try {
      const licensePlatesArray = formData.licensePlates
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0)

      const data = {
        spaceCode: formData.spaceCode,
        type: formData.type,
        unitId: formData.unitId,
        ownerId: formData.ownerId || null,
        currentUserId: formData.currentUserId || null,
        currentUserLeaseStart: formData.currentUserLeaseStart || null,
        currentUserLeaseEnd: formData.currentUserLeaseEnd || null,
        licensePlates: licensePlatesArray,
        note: formData.note.trim() || null,
      }

      if (isEditing && editingSpace) {
        await dispatch(updateParkingSpace({
          projectId: currentProject.id,
          spaceId: editingSpace.id,
          data: data as ParkingSpaceUpdateInput,
        })).unwrap()
        showSuccess('停車位更新成功')
      } else {
        await dispatch(createParkingSpace({
          projectId: currentProject.id,
          data: data as ParkingSpaceCreateInput,
        })).unwrap()
        showSuccess('停車位建立成功')
        // 新增後重新載入資料以更新統計
        dispatch(fetchParkingSpaces({
          projectId: currentProject.id,
          page: currentPage,
          limit: pageSize,
          type: typeFilter || undefined,
          search: debouncedSearch || undefined,
        }))
      }
      closeModal()
    } catch (error: any) {
      showError(isEditing ? '更新失敗' : '建立失敗', error.message || '請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (space: ParkingSpace) => {
    if (!currentProject?.id) return
    if (!confirm(`確定要刪除停車位「${space.spaceCode}」嗎？`)) return

    try {
      await dispatch(deleteParkingSpace({
        projectId: currentProject.id,
        spaceId: space.id,
      })).unwrap()
      showSuccess('停車位已刪除')
      // 刪除後重新載入資料以更新分頁資訊
      dispatch(fetchParkingSpaces({
        projectId: currentProject.id,
        page: currentPage,
        limit: pageSize,
        type: typeFilter || undefined,
        search: debouncedSearch || undefined,
      }))
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  // 活化配對：選 2 個 BIKE 後送出配對
  const reloadList = () => {
    if (!currentProject?.id) return
    dispatch(fetchParkingSpaces({
      projectId: currentProject.id,
      page: currentPage,
      limit: pageSize,
      type: typeFilter || undefined,
      search: debouncedSearch || undefined,
    }))
  }
  const toggleSpaceSelection = (space: ParkingSpace) => {
    if (space.type !== 'BIKE') {
      showError('只有自行車位可以做活化配對')
      return
    }
    if (space.activatedWithId || space.activatedFrom) {
      showError('此車位已在活化配對中')
      return
    }
    setSelectedSpaceIds(prev => {
      if (prev.includes(space.id)) return prev.filter(id => id !== space.id)
      if (prev.length >= 2) {
        showError('每次只能選擇 2 個自行車位配對')
        return prev
      }
      return [...prev, space.id]
    })
  }
  const confirmActivation = async () => {
    if (!currentProject?.id || selectedSpaceIds.length !== 2) return
    try {
      await activateBikePair(currentProject.id, { spaceIdA: selectedSpaceIds[0], spaceIdB: selectedSpaceIds[1] })
      showSuccess('已建立活化配對')
      setSelectedSpaceIds([])
      setActivationMode(false)
      reloadList()
    } catch (error: any) {
      showError('配對失敗', error.message || '請稍後再試')
    }
  }
  const handleDeactivatePair = async (space: ParkingSpace) => {
    if (!currentProject?.id) return
    const partnerCode = (space.activatedWith || space.activatedFrom)?.spaceCode
    if (!confirm(`確定要解除 ${space.spaceCode} 與 ${partnerCode} 的活化配對嗎？`)) return
    try {
      await deactivateBikePair(currentProject.id, { spaceId: space.id })
      showSuccess('已解除活化配對')
      closeModal()
      reloadList()
    } catch (error: any) {
      showError('解除失敗', error.message || '請稍後再試')
    }
  }

  // 租約歷史
  const [leaseHistoryItems, setLeaseHistoryItems] = useState<ParkingLease[]>([])
  const [leaseHistoryLoading, setLeaseHistoryLoading] = useState(false)
  const openLeaseHistory = async (space: ParkingSpace) => {
    if (!currentProject?.id) return
    setLeaseHistorySpace(space)
    setLeaseHistoryItems([])
    setLeaseHistoryLoading(true)
    try {
      const res = await getSpaceLeases(currentProject.id, space.id)
      if (res.success && res.data) setLeaseHistoryItems(res.data.leases)
    } catch (error: any) {
      showError('載入失敗', error.message || '請稍後再試')
    } finally {
      setLeaseHistoryLoading(false)
    }
  }

  // 新增排程租約
  const resetNewLeaseForm = () => {
    setNewLeaseData({
      leaseStartDate: '',
      leaseEndDate: '',
      unitId: '',
      contactId: '',
      note: '',
      applyToPair: false,
    })
    setNewLeaseUnitSearch('')
    setNewLeaseUnitDropdownOpen(false)
  }
  const handleSubmitNewLease = async () => {
    if (!currentProject?.id || !editingSpace) return
    if (!newLeaseData.leaseStartDate || !newLeaseData.leaseEndDate) {
      showError('請填寫租期起訖日')
      return
    }
    if (newLeaseData.leaseStartDate > newLeaseData.leaseEndDate) {
      showError('租期起始日不可晚於結束日')
      return
    }
    if (!newLeaseData.unitId) {
      showError('請選擇承租戶')
      return
    }
    setSavingLease(true)
    try {
      // 配對車位強制同步
      const isPaired = !!(editingSpace.activatedWith || editingSpace.activatedFrom)
      await createSpaceLease(currentProject.id, editingSpace.id, {
        leaseStartDate: newLeaseData.leaseStartDate,
        leaseEndDate: newLeaseData.leaseEndDate,
        unitId: newLeaseData.unitId,
        contactId: newLeaseData.contactId || null,
        note: newLeaseData.note.trim() || null,
        applyToPair: isPaired || newLeaseData.applyToPair,
      })
      showSuccess('已新增租約')
      // 重新載入租約歷史
      const res = await getSpaceLeases(currentProject.id, editingSpace.id)
      if (res.success && res.data) setLeaseHistoryItems(res.data.leases)
      // 刷新列表（cache 已被後端 recompute）
      reloadList()
      resetNewLeaseForm()
      setShowAddLeaseForm(false)
    } catch (error: any) {
      showError('新增租約失敗', error.message || '請稍後再試')
    } finally {
      setSavingLease(false)
    }
  }

  // Import Excel handlers
  const openImportModal = () => {
    setImportFile(null)
    setUpdateExisting(false)
    setImportResult(null)
    setShowImportModal(true)
  }

  const closeImportModal = () => {
    setShowImportModal(false)
    setImportFile(null)
    setImportResult(null)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImportFile(file)
      setImportResult(null)
    }
  }

  const handleImport = async () => {
    if (!currentProject?.id || !importFile) return

    setImporting(true)
    try {
      const response = await importParkingFromExcel(currentProject.id, importFile, updateExisting)
      if (response.success && response.data) {
        setImportResult(response.data)
        if (response.data.created > 0 || response.data.updated > 0) {
          // Refresh the parking spaces list with current pagination
          dispatch(fetchParkingSpaces({
            projectId: currentProject.id,
            page: 1, // 匯入後回到第一頁
            limit: pageSize,
            type: typeFilter || undefined,
            search: debouncedSearch || undefined,
          }))
          setCurrentPage(1)
          showSuccess(`匯入完成：新增 ${response.data.created} 筆，更新 ${response.data.updated} 筆`)
        }
      }
    } catch (error: any) {
      showError('匯入失敗', error.message || '請稍後再試')
    } finally {
      setImporting(false)
    }
  }

  // Import logs handlers
  const openImportLogsModal = async () => {
    if (!currentProject?.id) return
    setShowImportLogsModal(true)
    setLoadingLogs(true)
    try {
      const response = await getParkingImportLogs(currentProject.id, { limit: 50 })
      if (response.success && response.data) {
        setImportLogs(response.data.logs)
      }
    } catch (error: any) {
      showError('讀取匯入記錄失敗', error.message || '請稍後再試')
    } finally {
      setLoadingLogs(false)
    }
  }

  const closeImportLogsModal = () => {
    setShowImportLogsModal(false)
    setImportLogs([])
  }

  const viewLogDetail = async (logId: string) => {
    if (!currentProject?.id) return
    setLoadingLogDetail(true)
    setShowLogDetailModal(true)
    try {
      const response = await getParkingImportLogDetail(currentProject.id, logId)
      if (response.success && response.data) {
        setSelectedLogDetail(response.data.log)
      }
    } catch (error: any) {
      showError('讀取記錄詳情失敗', error.message || '請稍後再試')
      setShowLogDetailModal(false)
    } finally {
      setLoadingLogDetail(false)
    }
  }

  const closeLogDetailModal = () => {
    setShowLogDetailModal(false)
    setSelectedLogDetail(null)
  }

  const handleDeleteImportLog = async (logId: string) => {
    if (!currentProject?.id) return
    if (!confirm('確定要刪除此匯入記錄嗎？')) return

    try {
      await apiDeleteImportLog(currentProject.id, logId)
      setImportLogs(prev => prev.filter(log => log.id !== logId))
      showSuccess('記錄已刪除')
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  // Statistics - 使用伺服器回傳的統計資料
  const stats = {
    total: parkingSpacesPagination?.total || 0,
    car: parkingSpacesTypeCounts?.CAR || 0,
    motor: parkingSpacesTypeCounts?.MOTOR || 0,
    bike: parkingSpacesTypeCounts?.BIKE || 0,
    disabled: parkingSpacesTypeCounts?.DISABLED || 0,
  }

  // 計算分頁資訊
  const totalPages = parkingSpacesPagination?.totalPages || 1
  const startItem = ((currentPage - 1) * pageSize) + 1
  const endItem = Math.min(currentPage * pageSize, parkingSpacesPagination?.total || 0)

  // 產生分頁頁碼陣列
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages + 2) {
      // 如果總頁數較少，顯示所有頁碼
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // 顯示首頁
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      // 顯示當前頁附近的頁碼
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      // 顯示末頁
      pages.push(totalPages)
    }

    return pages
  }

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-600">您沒有檢視停車位管理的權限，請聯絡專案管理員。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-block mt-4 text-blue-600 hover:underline">回財務管理首頁</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectKey}/finance`}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <TruckIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">停車位管理</h1>
              <p className="text-sm text-gray-500">管理停車位分配與使用</p>
            </div>
          </div>
        </div>

        <div className="flex space-x-3 flex-wrap gap-y-2">
          {canManage && (
            activationMode ? (
              <>
                <span className="inline-flex items-center px-3 py-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md">
                  選擇中：{selectedSpaceIds.length}/2
                </span>
                <button
                  onClick={confirmActivation}
                  disabled={selectedSpaceIds.length !== 2}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                >
                  確認配對
                </button>
                <button
                  onClick={() => { setActivationMode(false); setSelectedSpaceIds([]) }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  取消
                </button>
              </>
            ) : (
              <button
                onClick={() => { setActivationMode(true); handleTypeFilterChange('BIKE') }}
                className="inline-flex items-center px-4 py-2 border border-emerald-300 shadow-sm text-sm font-medium rounded-md text-emerald-700 bg-white hover:bg-emerald-50"
                title="勾選 2 個自行車位配對為活化機車位"
              >
                活化配對
              </button>
            )
          )}
          <button
            onClick={openImportLogsModal}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ClockIcon className="w-4 h-4 mr-2" />
            匯入記錄
          </button>
          {canManage && (
            <button
              onClick={openImportModal}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
              匯入 Excel
            </button>
          )}
          {canManage && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              新增停車位
            </button>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">總停車位</div>
          <div className="text-2xl font-semibold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">汽車位</div>
          <div className="text-2xl font-semibold text-blue-600">{stats.car}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">機車位</div>
          <div className="text-2xl font-semibold text-green-600">{stats.motor}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">腳踏車位</div>
          <div className="text-2xl font-semibold text-yellow-600">{stats.bike}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">無障礙車位</div>
          <div className="text-2xl font-semibold text-purple-600">{stats.disabled}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col space-y-4">
          {/* 類型篩選 */}
          <div className="flex items-center space-x-4">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500">篩選類型:</span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleTypeFilterChange('')}
                className={`px-3 py-1 rounded-full text-sm ${
                  typeFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                全部
              </button>
              {Object.entries(parkingTypeLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => handleTypeFilterChange(key as ParkingType)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    typeFilter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 搜尋與分頁設定 */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋車位編號或車牌..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* 每頁筆數選擇 */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">每頁顯示:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} 筆</option>
                ))}
              </select>
            </div>

            {(searchQuery || typeFilter) && (
              <button
                onClick={clearSearch}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
              >
                清除篩選
              </button>
            )}
          </div>

          {/* 篩選結果計數 */}
          <div className="text-sm text-gray-500">
            {parkingSpacesPagination ? (
              <>
                顯示第 {startItem} - {endItem} 筆，共 {parkingSpacesPagination.total} 筆車位
              </>
            ) : (
              <>共 {parkingSpaces.length} 筆車位</>
            )}
          </div>
        </div>
      </div>

      {parkingSpacesError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{parkingSpacesError}</p>
        </div>
      )}

      {/* Parking Spaces Grid */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {parkingSpacesLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">載入中...</p>
          </div>
        ) : parkingSpaces.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {(typeFilter || searchQuery)
              ? '找不到符合條件的停車位'
              : '尚無停車位資料'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
              {parkingSpaces.map((space) => {
                const isSelected = selectedSpaceIds.includes(space.id)
                const isPairable = activationMode && space.type === 'BIKE' && !space.activatedWithId && !space.activatedFrom
                const partner = space.activatedWith || space.activatedFrom
                const isPaired = !!partner
                // 配對中較小編號的判斷（XB-100 < XB-101）
                const extractNum = (code: string) => parseInt(code.replace(/\D/g, ''), 10) || 0
                const isPairPrimary = isPaired ? extractNum(space.spaceCode) < extractNum(partner!.spaceCode) : true
                const pairKey = isPaired
                  ? (isPairPrimary ? space.id : (space.activatedWithId || space.activatedFrom?.id || ''))
                  : ''
                const isPairExpanded = pairKey ? expandedPairKeys.has(pairKey) : false
                // 配對中、非主卡、且非展開：隱藏（活化模式下也隱藏避免干擾）
                if (isPaired && !isPairPrimary && !isPairExpanded) return null
                const showStackEffect = isPaired && isPairPrimary && !isPairExpanded
                const togglePair = () => {
                  if (!pairKey) return
                  setExpandedPairKeys(prev => {
                    const next = new Set(prev)
                    if (next.has(pairKey)) next.delete(pairKey)
                    else next.add(pairKey)
                    return next
                  })
                }
                const cardClickHandler = activationMode && isPairable
                  ? () => toggleSpaceSelection(space)
                  : (isPaired && !activationMode ? togglePair : undefined)
                // 副卡（編號較大）展開時：套用滑出動畫
                const animateSecondaryIn = isPaired && !isPairPrimary && isPairExpanded
                // 主卡有配對：永遠 mount ghost，用 opacity/transform 控制平滑顯示
                const showGhosts = isPaired && isPairPrimary
                return (
                <div key={space.id} className={`relative ${animateSecondaryIn ? 'animate-pair-expand' : ''}`}>
                  {showGhosts && (
                    <>
                      {/* 後面那張卡（夥伴）的視覺暗示 */}
                      <div
                        className={`absolute inset-0 border border-gray-300 rounded-lg bg-white pointer-events-none transition-all duration-300 ${
                          isPairExpanded ? 'translate-x-0 translate-y-0 opacity-0' : 'translate-x-2 translate-y-2 opacity-100 shadow-sm'
                        }`}
                      />
                      <div
                        className={`absolute inset-0 border border-gray-300 rounded-lg bg-white pointer-events-none transition-all duration-300 ${
                          isPairExpanded ? 'translate-x-0 translate-y-0 opacity-0' : 'translate-x-1 translate-y-1 opacity-100'
                        }`}
                      />
                    </>
                  )}
                <div
                  onClick={cardClickHandler}
                  className={`relative border rounded-lg p-4 hover:shadow-md transition-all duration-300 bg-white ${
                    isSelected ? 'border-emerald-500 ring-2 ring-emerald-300 cursor-pointer bg-emerald-50' :
                    isPairable ? 'border-gray-300 cursor-pointer hover:border-emerald-400' :
                    activationMode && space.type === 'BIKE' && isPaired ? 'border-gray-200 opacity-60' :
                    isPaired && !activationMode ? 'border-emerald-200 cursor-pointer hover:border-emerald-400' :
                    'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${parkingTypeColors[space.type]}`}>
                        {parkingTypeLabels[space.type]}
                      </span>
                      {isSelected && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white">已選</span>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      {!activationMode && canManage && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditModal(space) }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="編輯"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      {!activationMode && canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(space) }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="刪除"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    {space.spaceCode}
                    {(space.activatedWith || space.activatedFrom) && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800"
                        title={`活化配對：${space.spaceCode} ↔ ${(space.activatedWith || space.activatedFrom)?.spaceCode}`}
                      >
                        ↔ {(space.activatedWith || space.activatedFrom)?.spaceCode}
                      </span>
                    )}
                  </h3>

                  <div className="space-y-2 text-sm">
                    {(space.type === 'BIKE' || space.type === 'DISABLED') ? (() => {
                      // 社區公用車位：space.unitId 直接指向承租戶（已由 lease cache 同步）
                      const isVacant = space.unit?.unitCode === 'COMMUNITY' || !space.unit
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-500">承租戶</span>
                            <span className="text-gray-900 font-medium">
                              {isVacant ? <span className="text-gray-400">空置</span> : space.unit?.unitCode}
                            </span>
                          </div>
                          {!isVacant && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-500">承租人</span>
                                <span className="text-gray-900 font-medium">
                                  {space.currentUser?.name || '-'}
                                </span>
                              </div>
                              {space.currentUserLeaseStart && space.currentUserLeaseEnd && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">租期</span>
                                  <span className="text-gray-900 font-medium text-xs">
                                    {String(space.currentUserLeaseStart).slice(0, 10)} ~ {String(space.currentUserLeaseEnd).slice(0, 10)}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {/* 預告下一位 */}
                          {space.leases && space.leases.length > 0 && (
                            <div className="mt-1 pt-1 border-t border-dashed border-gray-200 text-xs text-gray-500">
                              📅 下一位：{space.leases[0].unit?.unitCode || '-'} {space.leases[0].contact?.name || ''}（{String(space.leases[0].leaseStartDate).slice(0, 10)} 起）
                            </div>
                          )}
                        </>
                      )
                    })() : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500">戶號</span>
                          <span className="text-gray-900 font-medium">
                            {space.unit?.unitCode || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">車位所有人</span>
                          <span className="text-gray-900 font-medium">
                            {space.owner?.name || '-'}
                          </span>
                        </div>
                        {space.currentUser && space.currentUserId !== space.ownerId && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">目前使用者</span>
                            <span className="text-gray-900 font-medium">
                              {space.currentUser.name}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {space.licensePlates.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">車牌號碼</span>
                        <span className="text-gray-900 font-medium">
                          {space.licensePlates.join(', ')}
                        </span>
                      </div>
                    )}
                    {space.note && (
                      <div className="pt-1 text-xs text-gray-500 italic break-words" title={space.note}>
                        {space.note}
                      </div>
                    )}
                  </div>
                </div>
                </div>
                )
              })}
            </div>

            {/* 分頁控制 */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* 分頁資訊 */}
                  <div className="text-sm text-gray-500">
                    第 {currentPage} 頁，共 {totalPages} 頁
                  </div>

                  {/* 分頁按鈕 */}
                  <div className="flex items-center space-x-1">
                    {/* 上一頁 */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-2 text-sm rounded-md ${
                        currentPage === 1
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      上一頁
                    </button>

                    {/* 頁碼 */}
                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-400">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page as number)}
                          className={`px-3 py-2 text-sm rounded-md ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}

                    {/* 下一頁 */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-2 text-sm rounded-md ${
                        currentPage === totalPages
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      下一頁
                    </button>
                  </div>

                  {/* 跳轉頁面 */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">跳至</span>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value, 10)
                        if (page >= 1 && page <= totalPages) {
                          handlePageChange(page)
                        }
                      }}
                      className="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-500">頁</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 停車位操作紀錄 */}
      <FinanceAuditLog section="parking" title="停車位操作紀錄" limit={50} />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? '編輯停車位' : '新增停車位'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  車位編號 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.spaceCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, spaceCode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例如: B1-001"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  停車位類型 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as ParkingType }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(parkingTypeLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  關聯戶別 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={unitSearch || (formData.unitId ? (allUnits.find(u => u.id === formData.unitId)?.unitCode || '') : '')}
                    onChange={(e) => { setUnitSearch(e.target.value); setUnitDropdownOpen(true); setFormData(prev => ({ ...prev, unitId: '' })) }}
                    onFocus={() => setUnitDropdownOpen(true)}
                    placeholder="輸入戶號/地址搜尋..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {unitDropdownOpen && unitSearch && (
                    <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg">
                      {allUnits
                        .filter(u => {
                          const kw = unitSearch.toLowerCase()
                          return u.unitCode.toLowerCase().includes(kw) || (u.address || '').toLowerCase().includes(kw)
                        })
                        .slice(0, 30)
                        .map(u => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              // 自動帶入該戶第一位 OWNER 為「目前使用者」（BIKE / DISABLED 才適用）
                              let autoUserId = formData.currentUserId
                              if ((formData.type === 'BIKE' || formData.type === 'DISABLED') && u.unitCode !== 'COMMUNITY') {
                                const owner = contacts.find(c => c.unitContacts?.some((uc: any) => uc.unitId === u.id && uc.role === 'OWNER'))
                                if (owner) autoUserId = owner.id
                              }
                              setFormData(prev => ({ ...prev, unitId: u.id, currentUserId: autoUserId }))
                              setUnitSearch('')
                              setUnitDropdownOpen(false)
                            }}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${formData.unitId === u.id ? 'bg-blue-100' : ''}`}
                          >
                            <span className="font-mono font-medium">{u.unitCode}</span>
                            <span className="ml-2 text-xs text-gray-500">{u.address}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                {formData.unitId && !unitSearch && (() => {
                  const u = allUnits.find(x => x.id === formData.unitId)
                  return u ? <div className="mt-1 text-xs text-gray-500">{u.address}</div> : null
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  車位所有人
                </label>
                <SearchableContactSelect
                  value={formData.ownerId}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, ownerId: value }))
                  }}
                  contacts={contacts}
                  placeholder="選填（留空表示未指定法律持有人）"
                  allowEmpty
                  emptyLabel="（未指定）"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  目前使用者（若出租給他人）
                </label>
                <SearchableContactSelect
                  value={formData.currentUserId}
                  onChange={(value) => setFormData(prev => ({ ...prev, currentUserId: value }))}
                  contacts={contacts}
                  placeholder="同所有人"
                  allowEmpty
                  emptyLabel="同所有人"
                />
              </div>

              {formData.currentUserId && (() => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const end = formData.currentUserLeaseEnd ? new Date(formData.currentUserLeaseEnd) : null
                const expired = end && end < today
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">租約起始日</label>
                      <input
                        type="date"
                        value={formData.currentUserLeaseStart}
                        onChange={(e) => setFormData(prev => ({ ...prev, currentUserLeaseStart: e.target.value }))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        租約結束日
                        {expired && <span className="ml-2 text-red-600 font-medium">⚠ 已過期</span>}
                      </label>
                      <input
                        type="date"
                        value={formData.currentUserLeaseEnd}
                        onChange={(e) => setFormData(prev => ({ ...prev, currentUserLeaseEnd: e.target.value }))}
                        className={`w-full px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 ${expired ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
                      />
                    </div>
                  </div>
                )
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  車牌號碼
                </label>
                <input
                  type="text"
                  value={formData.licensePlates}
                  onChange={(e) => setFormData(prev => ({ ...prev, licensePlates: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ABC-1234, XYZ-5678（多個以逗號分隔）"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  備註
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                  rows={3}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例如：原為 #123、機車位轉作自行車位等..."
                />
              </div>

              {/* 解除活化配對（僅 BIKE 已配對才出現） */}
              {isEditing && editingSpace && editingSpace.type === 'BIKE' && (editingSpace.activatedWith || editingSpace.activatedFrom) && canManage && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-gray-700">已與</span>
                    <span className="font-mono font-semibold mx-1">{(editingSpace.activatedWith || editingSpace.activatedFrom)?.spaceCode}</span>
                    <span className="text-gray-700">配對為活化機車位</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeactivatePair(editingSpace)}
                    className="px-3 py-1.5 text-sm text-orange-700 border border-orange-300 rounded-md hover:bg-orange-50"
                  >
                    解除配對
                  </button>
                </div>
              )}

              {/* 租約歷史（BIKE / DISABLED 才有意義） */}
              {isEditing && editingSpace && (editingSpace.type === 'BIKE' || editingSpace.type === 'DISABLED') && (
                <div className="border border-gray-200 rounded-md">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600 flex items-center justify-between">
                    <span>租約歷史（{leaseHistoryItems.length} 筆）</span>
                    {canManage && !showAddLeaseForm && (
                      <button
                        type="button"
                        onClick={() => {
                          // 配對車位預設勾選並鎖定
                          const isPaired = !!(editingSpace?.activatedWith || editingSpace?.activatedFrom)
                          setNewLeaseData(prev => ({ ...prev, applyToPair: isPaired }))
                          setShowAddLeaseForm(true)
                        }}
                        className="text-xs px-2 py-1 text-emerald-700 border border-emerald-300 rounded hover:bg-emerald-50"
                      >
                        + 新增排程租約
                      </button>
                    )}
                  </div>

                  {/* 新增租約表單 */}
                  {showAddLeaseForm && canManage && (
                    <div className="p-3 bg-emerald-50 border-b border-emerald-100 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-0.5">租期起始 *</label>
                          <input
                            type="date"
                            value={newLeaseData.leaseStartDate}
                            onChange={(e) => setNewLeaseData(prev => ({ ...prev, leaseStartDate: e.target.value }))}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-0.5">租期結束 *</label>
                          <input
                            type="date"
                            value={newLeaseData.leaseEndDate}
                            onChange={(e) => setNewLeaseData(prev => ({ ...prev, leaseEndDate: e.target.value }))}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-0.5">承租戶 *</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={newLeaseUnitSearch || (newLeaseData.unitId ? (allUnits.find(u => u.id === newLeaseData.unitId)?.unitCode || '') : '')}
                            onChange={(e) => { setNewLeaseUnitSearch(e.target.value); setNewLeaseUnitDropdownOpen(true); setNewLeaseData(prev => ({ ...prev, unitId: '', contactId: '' })) }}
                            onFocus={() => setNewLeaseUnitDropdownOpen(true)}
                            placeholder="搜尋戶號/地址..."
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                          {newLeaseUnitDropdownOpen && newLeaseUnitSearch && (
                            <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg">
                              {allUnits
                                .filter(u => {
                                  const kw = newLeaseUnitSearch.toLowerCase()
                                  return u.unitCode.toLowerCase().includes(kw) || (u.address || '').toLowerCase().includes(kw)
                                })
                                .slice(0, 20)
                                .map(u => (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => {
                                      // 自動帶該戶第一位 OWNER
                                      const owner = contacts.find(c => c.unitContacts?.some((uc: any) => uc.unitId === u.id && uc.role === 'OWNER'))
                                      setNewLeaseData(prev => ({ ...prev, unitId: u.id, contactId: owner?.id || '' }))
                                      setNewLeaseUnitSearch('')
                                      setNewLeaseUnitDropdownOpen(false)
                                    }}
                                    className="block w-full text-left px-2 py-1 text-xs hover:bg-blue-50"
                                  >
                                    <span className="font-mono font-medium">{u.unitCode}</span>
                                    <span className="ml-2 text-gray-500">{u.address}</span>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                        {newLeaseData.contactId && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            自動帶入承租人：{contacts.find(c => c.id === newLeaseData.contactId)?.name || '-'}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-0.5">備註</label>
                        <input
                          type="text"
                          value={newLeaseData.note}
                          onChange={(e) => setNewLeaseData(prev => ({ ...prev, note: e.target.value }))}
                          placeholder="可選"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                      {(editingSpace.activatedWith || editingSpace.activatedFrom) && (
                        <label className="flex items-center text-xs text-gray-500 gap-1 cursor-not-allowed">
                          <input
                            type="checkbox"
                            checked
                            disabled
                            readOnly
                          />
                          同時為配對車位 {(editingSpace.activatedWith || editingSpace.activatedFrom)?.spaceCode} 建立相同租約（配對中強制同步）
                        </label>
                      )}
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => { setShowAddLeaseForm(false); resetNewLeaseForm() }}
                          className="px-3 py-1 text-xs text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          取消
                        </button>
                        <button
                          type="button"
                          onClick={handleSubmitNewLease}
                          disabled={savingLease}
                          className="px-3 py-1 text-xs text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {savingLease ? '儲存中...' : '建立租約'}
                        </button>
                      </div>
                    </div>
                  )}
                  {leaseHistoryLoading ? (
                    <div className="py-4 text-center text-gray-500 text-sm">載入中...</div>
                  ) : leaseHistoryItems.length === 0 ? (
                    <div className="py-4 text-center text-gray-400 text-sm">尚無租約紀錄</div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">租期</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">承租人</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">承租戶</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">狀態</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {leaseHistoryItems.map(lease => (
                            <tr key={lease.id}>
                              <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                                {String(lease.leaseStartDate).slice(0, 10)} ~ {String(lease.leaseEndDate).slice(0, 10)}
                              </td>
                              <td className="px-2 py-1.5">{lease.contact?.name || '-'}</td>
                              <td className="px-2 py-1.5 font-mono">{lease.unit?.unitCode || '-'}</td>
                              <td className="px-2 py-1.5">
                                <span className={`inline-flex px-1.5 py-0.5 rounded text-xs ${
                                  lease.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                  lease.status === 'ENDED' ? 'bg-gray-100 text-gray-600' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {lease.status === 'ACTIVE' ? '進行中' : lease.status === 'ENDED' ? '已結束' : '已解約'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* 編輯紀錄（僅編輯模式） */}
              {isEditing && editingSpace && (
                <FinanceAuditLog entityType="ParkingSpace" entityId={editingSpace.id} compact limit={30} />
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? '處理中...' : isEditing ? '更新' : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <DocumentArrowUpIcon className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  從 Excel 匯入車位資料
                </h3>
              </div>
              <button
                onClick={closeImportModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* File format description */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">支援的 Excel 格式</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• 工作表名稱：「社區實錄資料」（或含「車位」的工作表）</li>
                  <li>• 欄位：戶號、機車位、汽車位</li>
                  <li>• 汽車位可用換行分隔多個車位</li>
                  <li>• 「無車位」表示無車位</li>
                </ul>
              </div>

              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  選擇 Excel 檔案
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                {importFile && (
                  <p className="mt-2 text-sm text-gray-500">
                    已選擇：{importFile.name}
                  </p>
                )}
              </div>

              {/* Options */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="updateExisting" className="ml-2 block text-sm text-gray-900">
                  更新已存在的車位（覆蓋所有者資訊）
                </label>
              </div>

              {/* Import button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeImportModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? '匯入中...' : '開始匯入'}
                </button>
              </div>

              {/* Import results */}
              {importResult && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">匯入結果</h4>

                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-green-50 rounded-md p-3 text-center">
                      <div className="text-2xl font-bold text-green-600">{importResult.created}</div>
                      <div className="text-sm text-green-700">新增</div>
                    </div>
                    <div className="bg-blue-50 rounded-md p-3 text-center">
                      <div className="text-2xl font-bold text-blue-600">{importResult.updated}</div>
                      <div className="text-sm text-blue-700">更新</div>
                    </div>
                    <div className="bg-gray-50 rounded-md p-3 text-center">
                      <div className="text-2xl font-bold text-gray-600">{importResult.skipped}</div>
                      <div className="text-sm text-gray-700">跳過</div>
                    </div>
                  </div>

                  {/* Unit not found */}
                  {importResult.unitNotFound.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                      <div className="flex items-center mb-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
                        <h5 className="text-sm font-medium text-yellow-800">
                          找不到對應戶號（{importResult.unitNotFound.length} 筆）
                        </h5>
                      </div>
                      <div className="max-h-32 overflow-y-auto">
                        <ul className="text-sm text-yellow-700 space-y-1">
                          {importResult.unitNotFound.map((item, idx) => (
                            <li key={idx}>第 {item.row} 行：戶號「{item.unitCode}」</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Irregular data */}
                  {importResult.irregular.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                      <div className="flex items-center mb-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mr-2" />
                        <h5 className="text-sm font-medium text-orange-800">
                          需手動處理的資料（{importResult.irregular.length} 筆）
                        </h5>
                      </div>
                      <p className="text-xs text-orange-700 mb-2">
                        以下資料包含租賃或特殊備註，請手動處理：
                      </p>
                      <div className="max-h-40 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-orange-100">
                            <tr>
                              <th className="px-2 py-1 text-left text-orange-800">行號</th>
                              <th className="px-2 py-1 text-left text-orange-800">戶號</th>
                              <th className="px-2 py-1 text-left text-orange-800">資料</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-orange-200">
                            {importResult.irregular.map((item, idx) => (
                              <tr key={idx}>
                                <td className="px-2 py-1 text-orange-700">{item.row}</td>
                                <td className="px-2 py-1 text-orange-700 font-medium">{item.unitCode}</td>
                                <td className="px-2 py-1 text-orange-700 whitespace-pre-wrap">{item.data}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Errors */}
                  {importResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex items-center mb-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                        <h5 className="text-sm font-medium text-red-800">
                          錯誤訊息（{importResult.errors.length} 筆）
                        </h5>
                      </div>
                      <div className="max-h-32 overflow-y-auto">
                        <ul className="text-sm text-red-700 space-y-1">
                          {importResult.errors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Success message */}
                  {importResult.created > 0 || importResult.updated > 0 ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircleIcon className="w-5 h-5 mr-2" />
                      <span className="text-sm">匯入完成！可關閉此視窗或繼續匯入其他檔案。</span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Logs Modal */}
      {showImportLogsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold">車位匯入記錄</h3>
              </div>
              <button
                onClick={closeImportLogsModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {loadingLogs ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">載入中...</p>
                </div>
              ) : importLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  尚無匯入記錄
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">匯入時間</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">檔案名稱</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">新增</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">更新</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">找不到戶號</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">需手動處理</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {importLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(log.importedAt).toLocaleString('zh-TW')}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {log.fileName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-green-600 font-medium">
                            {log.createdCount}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-blue-600 font-medium">
                            {log.updatedCount}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            {log.unitNotFoundCount > 0 ? (
                              <span className="text-orange-600 font-medium">{log.unitNotFoundCount}</span>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            {log.irregularDataCount > 0 ? (
                              <span className="text-yellow-600 font-medium">{log.irregularDataCount}</span>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => viewLogDetail(log.id)}
                                className="text-blue-600 hover:text-blue-800"
                                title="查看詳情"
                              >
                                <EyeIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteImportLog(log.id)}
                                className="text-red-600 hover:text-red-800"
                                title="刪除記錄"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Log Detail Modal */}
      {showLogDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold">匯入記錄詳情</h3>
                {selectedLogDetail && (
                  <span className="text-sm text-gray-500">
                    ({new Date(selectedLogDetail.importedAt).toLocaleString('zh-TW')})
                  </span>
                )}
              </div>
              <button
                onClick={closeLogDetailModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[75vh]">
              {loadingLogDetail ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">載入中...</p>
                </div>
              ) : selectedLogDetail ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">{selectedLogDetail.totalRows}</div>
                      <div className="text-sm text-gray-500">總處理行數</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{selectedLogDetail.createdCount}</div>
                      <div className="text-sm text-gray-500">新增</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{selectedLogDetail.updatedCount}</div>
                      <div className="text-sm text-gray-500">更新</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-600">{selectedLogDetail.skippedCount}</div>
                      <div className="text-sm text-gray-500">跳過</div>
                    </div>
                  </div>

                  {/* Unit Not Found */}
                  {selectedLogDetail.unitNotFound.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <h4 className="text-md font-semibold text-orange-800 mb-3 flex items-center">
                        <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                        找不到對應戶號（{selectedLogDetail.unitNotFound.length} 筆）
                      </h4>
                      <div className="max-h-60 overflow-y-auto">
                        <table className="min-w-full divide-y divide-orange-200">
                          <thead className="bg-orange-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-orange-700 uppercase">行號</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-orange-700 uppercase">戶號</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-orange-100">
                            {selectedLogDetail.unitNotFound.map((item, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 text-sm text-orange-800">{item.row}</td>
                                <td className="px-3 py-2 text-sm text-orange-800 font-medium">{item.unitCode}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Irregular Data */}
                  {selectedLogDetail.irregularData.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="text-md font-semibold text-yellow-800 mb-3 flex items-center">
                        <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                        需手動處理的資料（{selectedLogDetail.irregularData.length} 筆）
                      </h4>
                      <div className="max-h-60 overflow-y-auto">
                        <table className="min-w-full divide-y divide-yellow-200">
                          <thead className="bg-yellow-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 uppercase">行號</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 uppercase">戶號</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 uppercase">資料內容</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 uppercase">原因</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-yellow-100">
                            {selectedLogDetail.irregularData.map((item, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 text-sm text-yellow-800">{item.row}</td>
                                <td className="px-3 py-2 text-sm text-yellow-800 font-medium">{item.unitCode}</td>
                                <td className="px-3 py-2 text-sm text-yellow-800">{item.data}</td>
                                <td className="px-3 py-2 text-sm text-yellow-700">{item.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Errors */}
                  {selectedLogDetail.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="text-md font-semibold text-red-800 mb-3 flex items-center">
                        <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                        錯誤訊息（{selectedLogDetail.errors.length} 筆）
                      </h4>
                      <div className="max-h-40 overflow-y-auto">
                        <ul className="text-sm text-red-700 space-y-1">
                          {selectedLogDetail.errors.map((error, idx) => (
                            <li key={idx} className="py-1">{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* No issues */}
                  {selectedLogDetail.unitNotFound.length === 0 &&
                    selectedLogDetail.irregularData.length === 0 &&
                    selectedLogDetail.errors.length === 0 && (
                    <div className="text-center py-8 text-green-600">
                      <CheckCircleIcon className="w-12 h-12 mx-auto mb-2" />
                      <p>此次匯入沒有任何異常資料</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={closeLogDetailModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default ParkingSpaceList
